import { useEffect, useMemo, useState } from 'react'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'

import { useBreadcrumb } from '@/app/Breadcrumb'
import { useMe } from '@/app/MeContextProvider'
import {
  Avatar,
  Button,
  Card,
  DataTable,
  Input,
  Modal,
  PageHeader,
  Select,
  StatusChip,
  Tag,
  useToast,
} from '@/design/components'
import type { Column } from '@/design/components'
import { ApiError } from '@/lib/api'
import { cn } from '@/lib/cn'
import { plural } from '@/lib/format'

import type { AdminRole, AdminUser, UserPatchBody } from '../api'
import { adminApi, adminKeys } from '../api'

const PAGE_SIZE = 25

export function UsersPage() {
  useBreadcrumb(['Administracja', 'Użytkownicy'])
  const toast = useToast()
  const queryClient = useQueryClient()
  const me = useMe()

  const [search, setSearch] = useState('')
  const [debounced, setDebounced] = useState('')
  const [roleFilter, setRoleFilter] = useState('')
  const [page, setPage] = useState(1)

  const [newUserOpen, setNewUserOpen] = useState(false)
  const [editing, setEditing] = useState<AdminUser | null>(null)
  const [credentials, setCredentials] = useState<{ user: AdminUser; password: string } | null>(null)
  // Akcje nieodwracalne wymagają potwierdzenia w modalu (bez natywnego window.confirm).
  const [confirming, setConfirming] = useState<ConfirmAction | null>(null)

  useEffect(() => {
    const timer = window.setTimeout(() => {
      setDebounced(search.trim())
      setPage(1)
    }, 250)
    return () => window.clearTimeout(timer)
  }, [search])

  const rolesQuery = useQuery({ queryKey: adminKeys.roles, queryFn: adminApi.roles })
  const roles = useMemo(() => rolesQuery.data ?? [], [rolesQuery.data])

  const query = {
    q: debounced || undefined,
    role_id: roleFilter ? Number(roleFilter) : undefined,
    page,
    size: PAGE_SIZE,
  }
  const usersQuery = useQuery({
    queryKey: adminKeys.users(query),
    queryFn: () => adminApi.users(query),
  })

  const refresh = async () => {
    await queryClient.invalidateQueries({ queryKey: ['admin', 'users'] })
    await queryClient.invalidateQueries({ queryKey: adminKeys.roles })
  }

  const patchMutation = useMutation({
    mutationFn: ({ userId, body }: { userId: number; body: UserPatchBody }) =>
      adminApi.patchUser(userId, body),
    onSuccess: async (user) => {
      await refresh()
      setEditing(null)
      setConfirming(null)
      toast.success('Zapisano zmiany', user.full_name)
    },
    onError: (error) => {
      setConfirming(null)
      toast.error('Nie zapisano zmian', error instanceof ApiError ? error.userMessage : undefined)
    },
  })

  const resetMutation = useMutation({
    mutationFn: (userId: number) => adminApi.resetPassword(userId),
    onSuccess: (data) => {
      setConfirming(null)
      if (data.temporary_password) {
        setCredentials({ user: data.user, password: data.temporary_password })
      }
      toast.success('Ustawiono hasło tymczasowe', data.user.full_name)
    },
    onError: (error) => {
      setConfirming(null)
      toast.error('Nie zresetowano hasła', error instanceof ApiError ? error.userMessage : undefined)
    },
  })

  const createMutation = useMutation({
    mutationFn: adminApi.createUser,
    onSuccess: async (data) => {
      await refresh()
      setNewUserOpen(false)
      if (data.temporary_password) {
        setCredentials({ user: data.user, password: data.temporary_password })
      }
      toast.success('Dodano użytkownika', data.user.full_name)
    },
    onError: (error) =>
      toast.error(
        'Nie udało się dodać użytkownika',
        error instanceof ApiError ? error.userMessage : undefined,
      ),
  })

  const columns: Column<AdminUser>[] = [
    {
      key: 'full_name',
      header: 'Użytkownik',
      render: (row) => (
        <div className="flex items-center gap-2.5">
          <Avatar name={row.full_name} size="md" tone={row.is_active ? 'navy' : 'light'} />
          <span className={cn('font-medium', row.is_active ? 'text-ink' : 'text-muted')}>
            {row.full_name}
          </span>
        </div>
      ),
    },
    { key: 'email', header: 'E-mail', render: (row) => <span className="text-muted">{row.email}</span> },
    { key: 'title', header: 'Tytuł', render: (row) => row.title ?? '—' },
    {
      key: 'roles',
      header: 'Role',
      render: (row) =>
        row.roles.length === 0 ? (
          <span className="text-muted">—</span>
        ) : (
          <div className="flex flex-wrap gap-1">
            {row.roles.map((role) => (
              <Tag key={role.id}>{role.name}</Tag>
            ))}
          </div>
        ),
    },
    {
      key: 'is_active',
      header: 'Status',
      render: (row) => (
        <StatusChip variant={row.is_active ? 'success' : 'neutral'} dot>
          {row.is_active ? 'aktywny' : 'nieaktywny'}
        </StatusChip>
      ),
    },
    {
      key: 'actions',
      header: '',
      align: 'right',
      width: '260px',
      render: (row) => (
        <div className="flex justify-end gap-1.5">
          <Button size="sm" variant="secondary" onClick={() => setEditing(row)}>
            Edytuj role
          </Button>
          <Button
            size="sm"
            variant="ghost"
            loading={resetMutation.isPending && resetMutation.variables === row.id}
            onClick={() => setConfirming({ kind: 'reset', user: row })}
          >
            Reset hasła
          </Button>
          <Button
            size="sm"
            variant={row.is_active ? 'ghost' : 'secondary'}
            disabled={row.id === me.user.id && row.is_active}
            title={
              row.id === me.user.id && row.is_active
                ? 'Nie możesz dezaktywować własnego konta'
                : undefined
            }
            onClick={() =>
              row.is_active
                ? setConfirming({ kind: 'deactivate', user: row })
                : patchMutation.mutate({ userId: row.id, body: { is_active: true } })
            }
          >
            {row.is_active ? 'Dezaktywuj' : 'Aktywuj'}
          </Button>
        </div>
      ),
    },
  ]

  const total = usersQuery.data?.total ?? 0

  return (
    <>
      <PageHeader
        title="Użytkownicy"
        subtitle={`${total} ${plural(total, 'konto', 'konta', 'kont')} w kancelarii · role nadajesz na ekranie „Role i uprawnienia"`}
        actions={
          <Button variant="primary" onClick={() => setNewUserOpen(true)}>
            Nowy użytkownik
          </Button>
        }
      />

      <Card className="mb-4">
        <div className="flex flex-wrap items-end gap-3 px-5 py-4">
          <div className="w-[280px]">
            <Input
              label="Szukaj"
              placeholder="Imię, nazwisko lub e-mail"
              value={search}
              onChange={(event) => setSearch(event.target.value)}
            />
          </div>
          <div className="w-[220px]">
            <Select
              inlineLabel="Rola"
              value={roleFilter}
              onChange={(event) => {
                setRoleFilter(event.target.value)
                setPage(1)
              }}
              options={[
                { value: '', label: 'wszystkie' },
                ...roles.map((role) => ({ value: String(role.id), label: role.name })),
              ]}
            />
          </div>
        </div>
      </Card>

      <DataTable<AdminUser>
        columns={columns}
        rows={usersQuery.data?.items ?? []}
        rowKey={(row) => row.id}
        loading={usersQuery.isPending}
        total={total}
        page={page}
        pageSize={PAGE_SIZE}
        onPageChange={setPage}
        empty="Brak użytkowników spełniających kryteria."
      />

      <UserFormModal
        open={newUserOpen}
        roles={roles}
        pending={createMutation.isPending}
        onClose={() => setNewUserOpen(false)}
        onSubmit={(body) => createMutation.mutate(body)}
      />

      <EditRolesModal
        user={editing}
        roles={roles}
        pending={patchMutation.isPending}
        onClose={() => setEditing(null)}
        onSubmit={(userId, body) => patchMutation.mutate({ userId, body })}
      />

      <ConfirmActionModal
        action={confirming}
        pending={resetMutation.isPending || patchMutation.isPending}
        onClose={() => setConfirming(null)}
        onConfirm={(action) => {
          if (action.kind === 'reset') resetMutation.mutate(action.user.id)
          else patchMutation.mutate({ userId: action.user.id, body: { is_active: false } })
        }}
      />

      <Modal
        open={credentials !== null}
        onClose={() => setCredentials(null)}
        title="Hasło tymczasowe"
        subtitle={credentials ? credentials.user.full_name : undefined}
        size="sm"
        footer={
          <div className="flex justify-end">
            <Button variant="primary" onClick={() => setCredentials(null)}>
              Rozumiem
            </Button>
          </div>
        }
      >
        <p className="text-base text-muted">
          Przekaż je użytkownikowi bezpiecznym kanałem. Hasła nie da się odczytać ponownie.
        </p>
        <p className="t-mono mt-3 select-all rounded border border-line bg-[#f8fafb] px-3 py-2 text-md">
          {credentials?.password}
        </p>
      </Modal>
    </>
  )
}

/* ------------------------------------------------------------------ modale */

/** Akcja nieodwracalna czekająca na potwierdzenie. */
interface ConfirmAction {
  kind: 'reset' | 'deactivate'
  user: AdminUser
}

interface ConfirmActionModalProps {
  action: ConfirmAction | null
  pending: boolean
  onClose: () => void
  onConfirm: (action: ConfirmAction) => void
}

/**
 * Potwierdzenie resetu hasła i dezaktywacji konta — obie akcje działają natychmiast
 * i nie da się ich cofnąć jednym kliknięciem, więc pytamy wprost o konkretną osobę.
 */
function ConfirmActionModal({ action, pending, onClose, onConfirm }: ConfirmActionModalProps) {
  const reset = action?.kind === 'reset'
  return (
    <Modal
      open={action !== null}
      onClose={onClose}
      size="sm"
      title={reset ? 'Zresetować hasło?' : 'Dezaktywować konto?'}
      subtitle={action ? `${action.user.full_name} · ${action.user.email}` : undefined}
      footer={
        <div className="flex justify-end gap-2">
          <Button variant="secondary" onClick={onClose}>
            Anuluj
          </Button>
          <Button
            variant="danger"
            loading={pending}
            onClick={() => action && onConfirm(action)}
          >
            {reset ? 'Zresetuj hasło' : 'Dezaktywuj konto'}
          </Button>
        </div>
      }
    >
      <p className="text-base text-muted">
        {reset
          ? 'Dotychczasowe hasło przestanie działać od razu. Wygenerujemy hasło tymczasowe i pokażemy je raz — trzeba je przekazać użytkownikowi.'
          : 'Użytkownik straci możliwość zalogowania się. Dane i historia jego działań zostają; konto można później aktywować ponownie.'}
      </p>
    </Modal>
  )
}

interface RolePickerProps {
  roles: AdminRole[]
  selected: number[]
  onToggle: (roleId: number) => void
}

function RolePicker({ roles, selected, onToggle }: RolePickerProps) {
  return (
    <div>
      <span className="t-label mb-1.5 block">Role</span>
      <div className="flex flex-col gap-1.5 rounded border border-line px-3 py-2.5">
        {roles.map((role) => (
          <label key={role.id} className="flex cursor-pointer items-center gap-2 text-base">
            <input
              type="checkbox"
              className="h-[14px] w-[14px] accent-navy-900"
              checked={selected.includes(role.id)}
              onChange={() => onToggle(role.id)}
            />
            <span className="font-medium text-ink">{role.name}</span>
            <span className="t-small text-muted">{role.description}</span>
          </label>
        ))}
      </div>
    </div>
  )
}

interface UserFormModalProps {
  open: boolean
  roles: AdminRole[]
  pending: boolean
  onClose: () => void
  onSubmit: (body: {
    email: string
    first_name: string
    last_name: string
    title: string | null
    role_ids: number[]
  }) => void
}

function UserFormModal({ open, roles, pending, onClose, onSubmit }: UserFormModalProps) {
  const [email, setEmail] = useState('')
  const [firstName, setFirstName] = useState('')
  const [lastName, setLastName] = useState('')
  const [title, setTitle] = useState('')
  const [roleIds, setRoleIds] = useState<number[]>([])

  useEffect(() => {
    if (open) {
      setEmail('')
      setFirstName('')
      setLastName('')
      setTitle('')
      setRoleIds([])
    }
  }, [open])

  const valid = email.includes('@') && firstName.trim() && lastName.trim()

  return (
    <Modal
      open={open}
      onClose={onClose}
      title="Nowy użytkownik"
      subtitle="Konto powstaje z hasłem tymczasowym — pokażemy je raz po zapisie."
      footer={
        <div className="flex justify-end gap-2">
          <Button variant="secondary" onClick={onClose}>
            Anuluj
          </Button>
          <Button
            variant="primary"
            loading={pending}
            disabled={!valid}
            onClick={() =>
              onSubmit({
                email: email.trim(),
                first_name: firstName.trim(),
                last_name: lastName.trim(),
                title: title.trim() || null,
                role_ids: roleIds,
              })
            }
          >
            Utwórz konto
          </Button>
        </div>
      }
    >
      <div className="flex flex-col gap-4">
        <Input
          label="E-mail"
          type="email"
          value={email}
          onChange={(event) => setEmail(event.target.value)}
          placeholder="imie.nazwisko@kancelaria.local"
          autoFocus
        />
        <div className="grid grid-cols-2 gap-3">
          <Input label="Imię" value={firstName} onChange={(e) => setFirstName(e.target.value)} />
          <Input label="Nazwisko" value={lastName} onChange={(e) => setLastName(e.target.value)} />
        </div>
        <Input
          label="Tytuł"
          value={title}
          onChange={(event) => setTitle(event.target.value)}
          placeholder="np. Senior Associate, adw."
        />
        <RolePicker
          roles={roles}
          selected={roleIds}
          onToggle={(roleId) =>
            setRoleIds((current) =>
              current.includes(roleId)
                ? current.filter((id) => id !== roleId)
                : [...current, roleId],
            )
          }
        />
      </div>
    </Modal>
  )
}

interface EditRolesModalProps {
  user: AdminUser | null
  roles: AdminRole[]
  pending: boolean
  onClose: () => void
  onSubmit: (userId: number, body: UserPatchBody) => void
}

function EditRolesModal({ user, roles, pending, onClose, onSubmit }: EditRolesModalProps) {
  const [roleIds, setRoleIds] = useState<number[]>([])
  const [title, setTitle] = useState('')

  useEffect(() => {
    if (user) {
      setRoleIds(user.roles.map((role) => role.id))
      setTitle(user.title ?? '')
    }
  }, [user])

  return (
    <Modal
      open={user !== null}
      onClose={onClose}
      title="Role użytkownika"
      subtitle={user?.full_name}
      footer={
        <div className="flex justify-end gap-2">
          <Button variant="secondary" onClick={onClose}>
            Anuluj
          </Button>
          <Button
            variant="primary"
            loading={pending}
            onClick={() =>
              user && onSubmit(user.id, { role_ids: roleIds, title: title.trim() || null })
            }
          >
            Zapisz
          </Button>
        </div>
      }
    >
      <div className="flex flex-col gap-4">
        <Input label="Tytuł" value={title} onChange={(event) => setTitle(event.target.value)} />
        <RolePicker
          roles={roles}
          selected={roleIds}
          onToggle={(roleId) =>
            setRoleIds((current) =>
              current.includes(roleId)
                ? current.filter((id) => id !== roleId)
                : [...current, roleId],
            )
          }
        />
      </div>
    </Modal>
  )
}
